import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import classnames from 'classnames';
import DarkMasonry from '@/assets/image/vip/dark_masonry.png';
import LightMasonry from '@/assets/image/vip/light_masonry.png';
import PayResultLogo from '@/assets/image/vip/payResult-logo.png';
import AvatarDefault from '@/assets/image/subscription/avatar-default.png';
import Svip from '@/assets/image/vip/svip.png';
import Vip from '@/assets/image/vip/vip.png';
import NoVip from '@/assets/image/vip/no-vip.png';
import TeamVip from '@/assets/image/team/team-vip.png';
import TeamNoVip from '@/assets/image/team/team-no-vip.png';
import { Method } from '@/services/user/index.d';
import {
  getIncomeSource,
  getVipTag,
  getDurationPeriod,
  closeVipSubscribeDialog,
  isWeb,
} from '@/utils/vip';
import LoginMethods from '../login-methods';
import meidouSuccess from '@/assets/image/meidou/pay-meidou-success.png';
import { getUrlParam } from '@/utils';
import {
  PurchaseScene,
  SubscribeType,
  isTeamSubscription,
  purchaseScene,
  subscribeType,
} from '@/constants';
import { ProductType } from '@meitu/subscribe-core';
import * as native from '@/utils/native';
import { useAtom } from 'jotai';
import {
  payProductInfoAtom,
  resultModalTitleAtom,
  seatsAtom,
  teamInfoAtom,
  userInfoAtom,
  vipInfoAtom,
  visibleBindPhoneAtom,
  visibleResultAtom,
} from '@/store';
import { compareVersions } from 'compare-versions';

import styles from './index.module.css';

interface IProps {
  theme: string;
}

const PayResult: React.FC<IProps> = ({ theme }) => {
  const type = getUrlParam('type');

  const [modalTitle] = useAtom(resultModalTitleAtom);
  const [userInfo] = useAtom(userInfoAtom);
  const [vipInfo] = useAtom(vipInfoAtom);
  const [teamInfo] = useAtom(teamInfoAtom);
  const [seats] = useAtom(seatsAtom);
  const [payProductInfo] = useAtom(payProductInfoAtom);
  const [, setVisibleBindPhone] = useAtom(visibleBindPhoneAtom);
  const [, setVisibleResult] = useAtom(visibleResultAtom);

  const [countdown, setCountdown] = useState<number>(10);

  const isMeidouOrder = ['meidou.meitu', 'meiye.meitu'].includes(payProductInfo?.commodityId);

  // 是否消耗型商品（按张数购买）
  const isConsumable = payProductInfo?.productType === ProductType.Consumable;
  const commodityConfig =
    payProductInfo?.commodityConfig && JSON.parse(payProductInfo.commodityConfig);
  const consumableCount = commodityConfig?.consumableCount;
  const consumableLimit = commodityConfig?.consumableLimit;

  if (consumableLimit) {
    consumableLimit.limitType = consumableLimit?.type;
  }

  const handleClose = async () => {
    const hasBindPhone = userInfo?.login_methods?.find((item) => item.icon === Method.PHONE);
    let shouldGuide = !hasBindPhone;
    if (getIncomeSource() !== 'MTXX-WEB' && shouldGuide) {
      // 校验版本号
      const version: string = await native.getVersion();
      // 相等时返回0，小于version时返回1，大于version时返回-1
      if (getIncomeSource() === 'MTXX-PC' && compareVersions('7.0.8.5', version) > 0) {
        // Windows ：7.0.8.5及其以上版本才开放
        shouldGuide = false;
      }
      if (getIncomeSource() === 'MTXX-MAC' && compareVersions('1.7.50', version) > 0) {
        // Mac ：1.7.50及其以上版本才开放
        shouldGuide = false;
      }
    }

    // 团队版不需要提示绑定手机号
    if (isTeamSubscription) {
      closeVipSubscribeDialog({
        success: true,
      });
      return;
    }

    if (shouldGuide) {
      // 未绑定手机号
      setVisibleBindPhone(true);
    } else {
      setVisibleResult(false);
      if (hasBindPhone) {
        closeVipSubscribeDialog({
          success: true,
        });
        return;
      }
    }
  };

  useEffect(() => {
    let timer: any = null;

    if (countdown <= 0) {
      handleClose();
      return;
    }

    timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [countdown]);

  const renderMeidouResult = (
    <>
      {isMeidouOrder ? <img className={styles.icon} src={meidouSuccess} /> : null}
      <div className={styles.title}>购买成功</div>
      <div className={styles.desc}>感谢您对我们的支持，我们将持续为您提供更多新的服务</div>
    </>
  );

  const vipIcon = isTeamSubscription ? TeamVip : Svip;
  const noVipIcon = isTeamSubscription ? TeamNoVip : NoVip;
  const avatar = isTeamSubscription ? teamInfo?.logo : userInfo?.avatar;
  const name = isTeamSubscription ? teamInfo?.name : userInfo?.screen_name;

  const renderSubResult = (
    <>
      {isConsumable && subscribeType === SubscribeType.CONSUMABLE ? (
        <img className={styles.logo} src={PayResultLogo} alt="" /> // 抠图
      ) : (
        <img className={styles.icon} src={theme === 'dark' ? DarkMasonry : LightMasonry} alt="" />
      )}
      <div className={styles.title}>
        {isConsumable
          ? `您已成功购买 ${type === 'svip' ? '美图秀秀批处理张数' : '美图抠图·下载张数'}：${
              consumableCount?.count
            }张`
          : modalTitle}
      </div>
      <div className={styles.info}>
        <div className={styles.avatarBox}>
          <img className={styles.avatar} src={avatar || AvatarDefault} alt="" />
          {!getVipTag(vipInfo).hasNoBuy ? (
            <img
              className={styles.vipIcon}
              src={getVipTag(vipInfo).svip ? vipIcon : getVipTag(vipInfo).vip ? Vip : noVipIcon}
              alt=""
            />
          ) : null}
        </div>
        <div className={styles.right}>
          <div className={styles.username}>{name || '-'}</div>
          <div className={styles.desc}>
            已绑定账号：
            {isTeamSubscription ? (
              `${teamInfo?.super_admin?.phone}`
            ) : (
              <LoginMethods userInfo={userInfo} />
            )}
          </div>
          {isConsumable ? (
            <div className={styles.desc}>
              购买张数：{consumableCount?.count}张，
              {getDurationPeriod(consumableLimit)}
            </div>
          ) : (
            <div className={styles.desc}>
              {/* 加购场景不展示 */}
              {purchaseScene !== PurchaseScene.EXTRA ? (
                <>
                  {isTeamSubscription ? '团队版会员有效期：' : `${isWeb ? 'VIP' : 'SVIP'}有效期：`}
                  {vipInfo?.vip_end_time
                    ? dayjs(vipInfo.vip_end_time * 1000).format('YYYY.MM.DD')
                    : ''}
                </>
              ) : null}
            </div>
          )}
          {isTeamSubscription && subscribeType === SubscribeType.SUBSCRIPTION ? (
            <div className={styles.desc}>购买席位数：{seats}位</div>
          ) : null}
        </div>
      </div>
    </>
  );

  const renderContent = () => {
    if (isMeidouOrder || subscribeType === SubscribeType.LOGO_UNLIMITED) {
      return renderMeidouResult;
    }

    return renderSubResult;
  };

  return (
    <div
      className={classnames(styles.module, {
        [styles.meidouModule]: isMeidouOrder,
        [styles.teamModule]: isTeamSubscription,
      })}
      style={{ borderRadius: getIncomeSource() !== 'MTXX-WEB' ? 0 : '' }}
    >
      <div
        className={classnames(
          styles.inner,
          isConsumable && subscribeType === SubscribeType.CONSUMABLE && styles.innerSingle
        )}
      >
        {renderContent()}
        <div className={styles.btn} onClick={handleClose}>
          我知道了（{countdown}s）
        </div>
      </div>
    </div>
  );
};

export default PayResult;
